/**
 * De lijst van helden (SPEC §7.1), met zoeken, filters, export en handmatig toevoegen.
 */

import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api, metHerkansing, type Helddossier, type Heldoverzichtsregel } from '../../api';
import { Datum, Euro, Knop, Laden, Melding, Niveaubadge, Veld, useTitel } from '../../componenten/basis';
import { NIVEAUNAMEN, NIVEAUS, type Niveau } from '../../lib/niveau';
import { STATUSNAMEN, type Heldstatus } from '../../lib/status';

export function Helden() {
  useTitel('Helden');
  const [regels, zetRegels] = useState<Heldoverzichtsregel[] | null>(null);
  const [fout, zetFout] = useState<string | null>(null);
  const [zoek, zetZoek] = useState('');
  const [status, zetStatus] = useState<Heldstatus | 'alle'>('alle');
  const [niveau, zetNiveau] = useState<Niveau | 'alle'>('alle');
  const [nieuw, zetNieuw] = useState(false);

  useEffect(() => {
    metHerkansing(() => api.haal<Heldoverzichtsregel[]>('/api/beheer/helden')).then(zetRegels).catch((f: Error) => zetFout(f.message));
  }, []);

  const getoond = useMemo(() => {
    if (!regels) return [];
    const q = zoek.trim().toLowerCase();
    return regels.filter(
      (r) =>
        (status === 'alle' || r.status === status) &&
        (niveau === 'alle' || r.niveau === niveau) &&
        (!q || r.naam.toLowerCase().includes(q) || r.email.toLowerCase().includes(q) || r.weergavenaam.toLowerCase().includes(q)),
    );
  }, [regels, zoek, status, niveau]);

  if (fout) return <Melding soort="fout">{fout}</Melding>;
  if (!regels) return <Laden />;

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <h1 className="text-3xl">Helden</h1>
        <div className="flex flex-wrap gap-2">
          <a href="/api/beheer/helden.csv" className="knop knop-stil knop-klein">
            Exporteer actieve helden (CSV)
          </a>
          <Knop klein onClick={() => zetNieuw((v) => !v)}>
            {nieuw ? 'Sluit' : 'Held toevoegen'}
          </Knop>
        </div>
      </div>

      {nieuw && <NieuweHeld />}

      <div className="kaart flex flex-wrap items-center gap-3 p-3">
        <input className="veld max-w-xs" placeholder="Zoek op naam of e-mail" value={zoek} onChange={(e) => zetZoek(e.target.value)} />
        <select className="veld w-auto" value={status} onChange={(e) => zetStatus(e.target.value as Heldstatus | 'alle')}>
          <option value="alle">Alle statussen</option>
          {(Object.keys(STATUSNAMEN) as Heldstatus[]).map((s) => (
            <option key={s} value={s}>
              {STATUSNAMEN[s]}
            </option>
          ))}
        </select>
        <select className="veld w-auto" value={niveau} onChange={(e) => zetNiveau(e.target.value as Niveau | 'alle')}>
          <option value="alle">Alle niveaus</option>
          {NIVEAUS.map((n) => (
            <option key={n} value={n}>
              {NIVEAUNAMEN[n]}
            </option>
          ))}
        </select>
        <span className="ml-auto text-sm text-inkt-zacht">
          {getoond.length} van {regels.length}
        </span>
      </div>

      <div className="kaart overflow-x-auto">
        <table className="tabel">
          <thead>
            <tr>
              <th>Naam</th>
              <th>Status</th>
              <th>Niveau</th>
              <th>Jaren</th>
              <th>Sinds</th>
              <th>Mandaat</th>
              <th>Open</th>
              <th>Laatste betaling</th>
            </tr>
          </thead>
          <tbody>
            {getoond.map((r) => (
              <tr key={r.id}>
                <td>
                  <Link to={`/beheer/helden/${r.id}`} className="font-medium">
                    {r.soort === 'bedrijf' ? r.weergavenaam : r.naam}
                  </Link>
                  <span className="block text-xs text-inkt-zacht">
                    {r.soort === 'bedrijf' && <>Bedrijf · {r.naam} · </>}
                    {r.email}
                    {r.soort === 'persoon' && r.weergavenaam !== r.naam && <> · op de muur als “{r.weergavenaam}”</>}
                    {!r.toon_op_muur && <> · anoniem</>}
                    {r.jaarbijdrage_cent && <> · <Euro cent={r.jaarbijdrage_cent} /> per jaar</>}
                  </span>
                </td>
                <td>
                  <Status status={r.status} />
                </td>
                <td>
                  <Niveaubadge niveau={r.niveau} />
                </td>
                <td className="tabular-nums">{r.betaaldeJaren}</td>
                <td className="text-inkt-zacht"><Datum waarde={r.lid_sinds} /></td>
                <td className="text-inkt-zacht">{r.heeft_mandaat ? 'ja' : '-'}</td>
                <td>{r.open_cent > 0 ? <span className="text-rood"><Euro cent={r.open_cent} /></span> : <span className="text-inkt-stil">-</span>}</td>
                <td className="text-inkt-zacht"><Datum waarde={r.laatste_betaling_op} /></td>
              </tr>
            ))}
            {getoond.length === 0 && (
              <tr>
                <td colSpan={8} className="py-10 text-center text-inkt-zacht">
                  Geen helden die hieraan voldoen.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function Status({ status }: { status: Heldstatus }) {
  const kleur: Record<Heldstatus, string> = {
    actief: 'text-groen',
    aangemeld: 'text-inkt-zacht',
    verlopen: 'text-rood',
    opgezegd: 'text-inkt-stil',
  };
  return <span className={`text-sm ${kleur[status]}`}>{STATUSNAMEN[status]}</span>;
}

function NieuweHeld() {
  const navigeer = useNavigate();
  const [invoer, zetInvoer] = useState({ soort: 'persoon' as 'persoon' | 'bedrijf', naam: '', email: '', weergavenaam: '', regel: '', telefoon: '', toon_op_muur: true, betaald: true, afkoop: '' });
  const [bezig, zetBezig] = useState(false);
  const [fout, zetFout] = useState<string | null>(null);

  async function verstuur(e: FormEvent) {
    e.preventDefault();
    zetBezig(true);
    zetFout(null);
    try {
      const dossier = await api.stuur<Helddossier>('/api/beheer/helden', { ...invoer, afkoop: invoer.afkoop || null });
      navigeer(`/beheer/helden/${dossier.id}`);
    } catch (f) {
      zetFout((f as Error).message);
      zetBezig(false);
    }
  }

  return (
    <form onSubmit={verstuur} className="kaart space-y-4 p-5">
      <h2 className="text-xl">Held met de hand toevoegen</h2>
      <p className="text-sm text-inkt-zacht">
        Voor wie contant of per overschrijving betaalt. Zonder incassomandaat: deze held krijgt elk jaar een betaalverzoek per mail.
      </p>
      <div className="grid gap-4 sm:grid-cols-2">
        <Veld etiket="Soort">
          <select className="veld" value={invoer.soort} onChange={(e) => zetInvoer({ ...invoer, soort: e.target.value as 'persoon' | 'bedrijf' })}>
            <option value="persoon">Persoon</option>
            <option value="bedrijf">Bedrijf</option>
          </select>
        </Veld>
        <Veld etiket={invoer.soort === 'bedrijf' ? 'Contactpersoon' : 'Naam'}>
          <input className="veld" required value={invoer.naam} onChange={(e) => zetInvoer({ ...invoer, naam: e.target.value })} />
        </Veld>
        <Veld etiket="E-mail">
          <input className="veld" type="email" required value={invoer.email} onChange={(e) => zetInvoer({ ...invoer, email: e.target.value })} />
        </Veld>
        <Veld etiket={invoer.soort === 'bedrijf' ? 'Bedrijfsnaam' : 'Naam op de muur'} hulp={invoer.soort === 'bedrijf' ? 'Het logo kan daarna in het dossier.' : 'Leeg = de naam'}>
          <input className="veld" required={invoer.soort === 'bedrijf'} value={invoer.weergavenaam} onChange={(e) => zetInvoer({ ...invoer, weergavenaam: e.target.value })} />
        </Veld>
        <Veld etiket="Regel eronder">
          <input className="veld" maxLength={80} value={invoer.regel} onChange={(e) => zetInvoer({ ...invoer, regel: e.target.value })} />
        </Veld>
        <Veld etiket="Telefoon">
          <input className="veld" value={invoer.telefoon} onChange={(e) => zetInvoer({ ...invoer, telefoon: e.target.value })} />
        </Veld>
        <Veld etiket="Afgekocht niveau (als meebetaald)">
          <select className="veld" value={invoer.afkoop} onChange={(e) => zetInvoer({ ...invoer, afkoop: e.target.value })} disabled={!invoer.betaald}>
            <option value="">Geen - brons</option>
            <option value="zilver">Zilver</option>
            <option value="goud">Goud</option>
          </select>
        </Veld>
      </div>
      <div className="flex flex-wrap gap-6 text-sm">
        <label className="flex items-center gap-2">
          <input type="checkbox" className="accent-goud-500" checked={invoer.toon_op_muur} onChange={(e) => zetInvoer({ ...invoer, toon_op_muur: e.target.checked })} />
          Met naam op de muur
        </label>
        <label className="flex items-center gap-2">
          <input type="checkbox" className="accent-goud-500" checked={invoer.betaald} onChange={(e) => zetInvoer({ ...invoer, betaald: e.target.checked })} />
          Eerste jaarbijdrage is al betaald (contant/bank)
        </label>
      </div>
      {!invoer.betaald && <Melding>Er gaat meteen een betaalverzoek per mail uit; de held komt op de muur zodra dat betaald is.</Melding>}
      {fout && <Melding soort="fout">{fout}</Melding>}
      <Knop type="submit" bezig={bezig}>
        Toevoegen
      </Knop>
    </form>
  );
}
